'use client';

/**
 * IntentSchedulesCard — explorer-surface tree of ingested schedules.
 *
 * Parallel implementation. Same data contracts the legacy
 * `intent-schedules-section.tsx` consumes — `intent_list`,
 * `intent_lines`, `intent_grid` BFF strategies; `intent-store` and
 * `graph-selection-store` for state — but a fresh component owned by
 * mode3. Legacy section stays in place for the legacy `intent`
 * mode; mode3 doesn't import it.
 *
 * Schedule click → expands + fetches lines + fires grid + selects
 * the Intent in graph (so the rail's brief/lines/match-quality
 * cards paint).
 * Line click → toggles `selectedLineSid` (canvas grid highlights).
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_schedules"
 *
 * @component_role: Card
 * @component_type: Explorer Card
 * @domain: Explorer / Intent
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useIntentList, useIntentLines, useIntentGrid } from '@/lib/api/hooks';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { usePinnedCostPlanStore } from '@/lib/stores/pinned-costplan-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { text, surfaceBase } from '@/lib/design/tokens.x';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { IntentScheduleCard } from './schedule-card';
import type { IntentLineRow, IntentSummary } from '@/lib/api/types';

const INTENT_DETAILS_STRATEGY = 'intent_details';
const INTENT_LINES_STRATEGY = 'intent_lines';

export function IntentSchedulesCard() {
  const L = useExplorerLabels('intent');
  const queryClient = useQueryClient();
  const listStrategy = useCapabilityStore((s) => s.strategyName('intent', 'list_intents'));
  const linesStrategy = useCapabilityStore((s) => s.strategyName('intent', 'list_intent_lines'));
  const gridStrategy = useCapabilityStore((s) => s.strategyName('intent', 'grid'));

  const list = useIntentList(listStrategy ?? 'intent_list');
  const lines = useIntentLines(linesStrategy ?? 'intent_lines');
  const grid = useIntentGrid(gridStrategy ?? 'intent_grid');

  const intentSid = useIntentStore((s) => s.intentSid);
  const gridResponse = useIntentStore((s) => s.gridResponse);
  const gridResponseLoadedAt = useIntentStore((s) => s.gridResponseLoadedAt);
  const selectedLineSid = useIntentStore((s) => s.selectedLineSid);
  const setGridResponse = useIntentStore((s) => s.setGridResponse);
  const selectLine = useIntentStore((s) => s.selectLine);
  const setLoading = useIntentStore((s) => s.setLoading);
  const setError = useIntentStore((s) => s.setError);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  // Mode key for the capture envelope — the active explorer view (mode3).
  const activeExplorerView = useLayoutStore((s) => s.activeExplorerView);
  // Same signal ProductTile already reads to gate add-to-line — scopes
  // the picked-state marker on reload to the buyer's own plan, not
  // every plan under the Intent. docs/mcp_refined_ux.md §3.
  const pinnedMcpId = usePinnedCostPlanStore((s) => s.pinnedMcpId);

  // Cache TTL for the intent_grid mutation result. Re-clicking a
  // schedule within this window reuses the cached gridResponse from
  // intent-store rather than re-firing the (expensive) full LLM +
  // Pareto shortlist build. The Brief card's refresh button clears
  // this cache when the user wants a fresh build.
  const GRID_CACHE_TTL_MS = 10 * 60 * 1000;

  // Per-schedule expansion + cached lines (instant expand on re-click).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [linesBySid, setLinesBySid] = useState<Record<string, IntentLineRow[]>>({});

  const items = list.data?.items ?? [];

  // The roster query backs every schedule card's content. Shared by all
  // instances, so the capture envelope dedups to one payload (the list)
  // behind N scene entries. Matches useIntentList's key shape exactly
  // (['strategy-data', strategy]).
  const listStrategyName = listStrategy ?? 'intent_list';
  const rosterQueryKey = useMemo(
    () => ['strategy-data', listStrategyName],
    [listStrategyName],
  );

  // Collection export — the roster (which schedules exist). The
  // per-schedule children register their own exports; this is the
  // container level of the recursion. Called before the early returns
  // per Rules of Hooks.
  useCardExport(
    useMemo(
      () => ({
        label: L.schedules_section_header ?? 'Schedules',
        content_key: 'intent_schedules',
        status: (list.isLoading ? 'loading' : 'loaded') as 'loading' | 'loaded',
        query_key: rosterQueryKey,
        text: items.map((it) => it.name || it.intent_sid).join('\n'),
        json: {
          count: items.length,
          schedules: items.map((it) => ({
            intent_sid: it.intent_sid,
            name: it.name ?? '',
            line_count: it.line_count,
          })),
        },
      }),
      [items, list.isLoading, rosterQueryKey, L],
    ),
  );

  const loadGrid = (item: IntentSummary, lineRows: IntentLineRow[]) => {
    if (!lineRows.length) return;
    setLoading(true);
    grid.mutate(
      {
        intent_sid: item.intent_sid,
        queries: lineRows.map((r) => ({ line_sid: r.line_sid, text: r.name })),
        top_k_per_line: 5,
        costplan_sid: pinnedMcpId,
      },
      {
        onSuccess: (data) => setGridResponse(data),
        onError: (err) => {
          setError(err);
          toast.error(`Grid failed: ${err.message}`);
        },
      },
    );
  };

  const handleScheduleClick = async (item: IntentSummary) => {
    // Clicking a schedule should bring the user out of any explorer
    // sub-mode (e.g. add_schedule) so the canvas swaps to the grid
    // paint. Cards-everywhere primitive — no tab close, no closeTab.
    setExplorerSelection(null);

    const isOpen = !!expanded[item.intent_sid];
    setExpanded((e) => ({ ...e, [item.intent_sid]: !isOpen }));

    // Propagate the Intent into graph selection so the rail's
    // brief/lines/match-quality cards paint with the full summary.
    selectNode({
      id: item.intent_sid,
      label: item.name || item.intent_sid,
      type: 'intent',
      properties: item as unknown as Record<string, unknown>,
    });

    let lineRows = linesBySid[item.intent_sid];
    if (!lineRows) {
      try {
        const res = await lines.mutateAsync({ intent_sid: item.intent_sid });
        lineRows = res.lines;
        setLinesBySid((m) => ({ ...m, [item.intent_sid]: lineRows }));
      } catch (err) {
        toast.error(`Lines failed: ${(err as Error).message}`);
        return;
      }
    }

    // Skip grid.mutate if we already have a fresh grid for this intent
    // (within TTL). Re-firing intent_grid for the same schedule is the
    // expensive redundant work the user reported; the Brief card's
    // refresh button is the explicit escape hatch.
    const cachedForThisIntent =
      gridResponse?.intent_sid === item.intent_sid &&
      gridResponseLoadedAt !== null &&
      Date.now() - gridResponseLoadedAt < GRID_CACHE_TTL_MS;
    if (!cachedForThisIntent) {
      loadGrid(item, lineRows);
    }
  };

  const handleLineClick = (lineSid: string) => {
    selectLine(selectedLineSid === lineSid ? null : lineSid);
  };

  // Refresh — clears the cached grid + invalidates the rail's
  // cached intent_details query (Brief / Lines / MatchQuality cards
  // dedup onto one refetch via React Query), then re-fires
  // intent_lines + intent_grid for this schedule. One click
  // rebuilds everything Intent-scoped from scratch.
  const handleRefresh = async (item: IntentSummary) => {
    void queryClient.invalidateQueries({
      queryKey: ['strategy-data', INTENT_DETAILS_STRATEGY, { intent_sid: item.intent_sid }],
    });
    void queryClient.invalidateQueries({
      queryKey: ['strategy-data', INTENT_LINES_STRATEGY, { intent_sid: item.intent_sid }],
    });
    setGridResponse(null);
    // Re-fetch lines fresh; clear the local cache so the grid call
    // sees the new line set if anything changed server-side.
    let lineRows: IntentLineRow[];
    try {
      const res = await lines.mutateAsync({ intent_sid: item.intent_sid });
      lineRows = res.lines;
      setLinesBySid((m) => ({ ...m, [item.intent_sid]: lineRows }));
    } catch (err) {
      toast.error(`Refresh failed: ${(err as Error).message}`);
      return;
    }
    loadGrid(item, lineRows);
  };

  if (list.isLoading) {
    return (
      <div>
        <Header label={L.schedules_section_header ?? 'Schedules'} />
        <div className="px-2 py-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          {L.schedules_loading ?? 'Loading schedules…'}
        </div>
      </div>
    );
  }

  if (list.isError) {
    return (
      <div>
        <Header label={L.schedules_section_header ?? 'Schedules'} />
        <div className="px-2 py-2 text-xs text-destructive">{list.error.message}</div>
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div>
      <Header label={L.schedules_section_header ?? 'Schedules'} count={items.length} />
      <div className="space-y-1.5">
        {items.map((item) => {
          const isActive = intentSid === item.intent_sid;
          return (
            // Each schedule is a card, generated from the roster (not the
            // matrix). The CardPlacementProvider injects surface + a
            // per-instance slot_id so the N same-kind cards stay
            // distinguishable in the capture envelope.
            <CardPlacementProvider
              key={item.intent_sid}
              value={{ surface: 'explorer', slot_id: `intent_schedule:${item.intent_sid}` }}
            >
              <IntentScheduleCard
                item={item}
                isActive={isActive}
                isOpen={!!expanded[item.intent_sid]}
                isRefreshing={isActive && (lines.isPending || grid.isPending)}
                onSelect={() => handleScheduleClick(item)}
                // Refresh affordance only on the currently-active schedule —
                // refresh is the "redo what I just did" action, scoped to
                // the schedule whose data is currently surfaced.
                onRefresh={isActive ? () => handleRefresh(item) : undefined}
                lineRows={linesBySid[item.intent_sid]}
                selectedLineSid={selectedLineSid}
                onLineClick={handleLineClick}
                rosterQueryKey={rosterQueryKey}
                mode={activeExplorerView}
                menuLabel={L.schedule_menu_label}
                copyLabel={L.action_copy_schedule}
                downloadCaptureLabel={L.action_download_capture}
              />
            </CardPlacementProvider>
          );
        })}
      </div>
    </div>
  );
}

function Header({ label, count }: { label: string; count?: number }) {
  return (
    <div className="px-1 pb-1.5 flex items-baseline gap-2" style={{ fontSize: surfaceBase.canvas }}>
      <h3 className={text.eyebrow}>{label}</h3>
      {count != null && <span className={text.muted}>{count}</span>}
    </div>
  );
}

